import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";
import { useAuth } from "../contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  LayoutDashboard,
  Users,
  FolderOpen,
  UserCheck,
  FileText,
  Calendar,
  BarChart3,
  Settings,
  Menu,
  Leaf,
  Bell,
  Search,
  Wrench,
  Palette,
  Mail,
  Zap,
  Receipt,
  Send,
  Loader2,
  Store,
  ListTodo,
  PackagePlus,
  X,
  HardHat,
  PhoneIncoming,
} from "lucide-react";
import { Input } from "./ui/input";
import { searchService } from "../services/searchService";
import NotificationsBell from "./NotificationsBell";

const navigation = [
  { name: "Dashboard", href: "/", icon: LayoutDashboard },
  { name: "Leads", href: "/leads", icon: Users },
  { name: "Customers", href: "/customers", icon: UserCheck },
  { name: "Projects", href: "/projects", icon: FolderOpen },
  { name: "Production", href: "/production", icon: HardHat },
  { name: "Invoices", href: "/invoices", icon: Receipt },
  { name: "Send Email", href: "/send-email", icon: Send },
  { name: "Estimates", href: "/estimates", icon: FileText },
  { name: "Schedule", href: "/schedule", icon: Calendar },
  { name: "Callbacks", href: "/callbacks", icon: PhoneIncoming },
  { name: "Designers", href: "/designers", icon: Palette },
  { name: "Subcontractors", href: "/subcontractors", icon: Wrench },
  { name: "Vendors", href: "/vendors", icon: Store },
  { name: "Tasks", href: "/tasks", icon: ListTodo },
  { name: "Automations", href: "/automations", icon: Zap },
  { name: "Email Templates", href: "/email-templates", icon: Mail },
  { name: "Reports", href: "/reports", icon: BarChart3 },
  { name: "Settings", href: "/settings", icon: Settings },
];

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  
  // Global search states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Filter navigation based on user role
  const filteredNavigation = navigation.filter(item => {
    // User role restrictions - only allow specific pages
    if (user?.role === 'user') {
      const allowedPages = [
        '/',
        '/customers',
        '/projects',
        '/send-email',
        '/estimates',
        '/schedule',
        '/tasks',
        '/settings'
      ];
      return allowedPages.includes(item.href);
    }
    
    // Admin-only routes
    if ((item.href === '/automations' || item.href === '/email-templates') && !isAdmin()) {
      return false;
    }
    return true;
  });

  const getUserInitials = (name) => {
    return name
      ?.split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase() || 'U';
  };

  // Handle search with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length < 2) {
      setSearchResults(null);
      setShowSearchResults(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await searchService.globalSearch(searchQuery.trim());
        setSearchResults(response.data);
        setShowSearchResults(true);
      } catch (error) {
        console.error("Search failed:", error);
        setSearchResults(null);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearchResultClick = (type, id) => {
    setShowSearchResults(false);
    setSearchQuery("");
    setSearchResults(null);
    
    if (type === "project") {
      navigate(`/projects?openId=${id}`);
    } else if (type === "customer") {
      navigate(`/customers?openId=${id}`);
    } else if (type === "lead") {
      navigate(`/leads?openId=${id}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed left-0 top-0 h-full w-64 z-50 bg-white shadow-xl transform transition-transform duration-300 ease-in-out",
          "md:translate-x-0 md:shadow-lg",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex items-center justify-between h-14 sm:h-16 px-4" style={{ backgroundColor: '#4478bb' }}>
          <div className="flex items-center">
            <Leaf className="h-8 w-8 text-white" />
            <span className="ml-2 text-xl font-bold text-white">
              Reliable CRM
            </span>
          </div>
          {/* Close button for mobile */}
          <button
            className="md:hidden h-10 w-10 flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="mt-4 px-3 overflow-y-auto" style={{maxHeight: 'calc(100vh - 4rem)'}}>
          <ul className="space-y-1">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={cn(
                      "flex items-center px-4 h-12 text-base md:text-sm md:h-10 font-medium rounded-lg transition-colors",
                      isActive
                        ? "bg-brand-light text-brand-700 border-r-2 border-brand-600"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    )}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="mr-3 h-5 w-5" />
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      {/* Main content */}
      <div className="md:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-30 flex h-14 sm:h-16 shrink-0 items-center gap-x-2 sm:gap-x-4 border-b border-gray-200 bg-white px-3 sm:px-4 shadow-sm md:px-6 lg:px-8">
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden h-10 w-10 flex-shrink-0 p-0"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </Button>

          <div className="flex flex-1 gap-x-2 sm:gap-x-4 self-stretch md:gap-x-6">
            <div ref={searchRef} className="relative flex flex-1 items-center">
              <Search className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-400 ml-3" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search projects, customers, or leads..."
                className="w-full pl-10 pr-4 h-10 border-gray-300 focus:border-emerald-500 focus:ring-emerald-500 text-sm"
              />
              {searchLoading && (
                <Loader2 className="absolute inset-y-0 right-0 h-full w-5 text-gray-400 mr-3 animate-spin" />
              )}
              
              {/* Search Results Dropdown */}
              {showSearchResults && searchResults && (
                <div className="absolute top-full mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-y-auto z-50">
                  {searchResults.total_results === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      No results found for "{searchQuery}"
                    </div>
                  ) : (
                    <div className="py-2">
                      {/* Projects Section */}
                      {searchResults.projects && searchResults.projects.length > 0 && (
                        <div className="mb-2">
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-50">
                            Projects ({searchResults.projects.length})
                          </div>
                          {searchResults.projects.map((project) => (
                            <button
                              key={project.id}
                              onClick={() => handleSearchResultClick("project", project.id)}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-start gap-3"
                            >
                              <FolderOpen className="h-5 w-5 text-brand-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 truncate">{project.name}</div>
                                <div className="text-sm text-gray-500 truncate">
                                  {project.customer_name} • {project.jobsite_address || project.address || "No address"}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Customers Section */}
                      {searchResults.customers && searchResults.customers.length > 0 && (
                        <div className="mb-2">
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-50">
                            Customers ({searchResults.customers.length})
                          </div>
                          {searchResults.customers.map((customer) => (
                            <button
                              key={customer.id}
                              onClick={() => handleSearchResultClick("customer", customer.id)}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-start gap-3"
                            >
                              <UserCheck className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 truncate">
                                  {customer.first_name && customer.last_name 
                                    ? `${customer.first_name} ${customer.last_name}` 
                                    : customer.name || 'Unknown'}
                                </div>
                                <div className="text-sm text-gray-500 truncate">
                                  {customer.email || customer.phone || "No contact info"}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Leads Section */}
                      {searchResults.leads && searchResults.leads.length > 0 && (
                        <div>
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-50">
                            Leads ({searchResults.leads.length})
                          </div>
                          {searchResults.leads.map((lead) => (
                            <button
                              key={lead.id}
                              onClick={() => handleSearchResultClick("lead", lead.id)}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-start gap-3"
                            >
                              <Users className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 truncate">
                                  {lead.first_name && lead.last_name 
                                    ? `${lead.first_name} ${lead.last_name}` 
                                    : lead.name || 'Unknown'}
                                </div>
                                <div className="text-sm text-gray-500 truncate">
                                  {lead.email || lead.phone || "No contact info"}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-x-2 sm:gap-x-4 md:gap-x-6 flex-shrink-0">
              <NotificationsBell />

              <div className="hidden md:block md:h-6 md:w-px md:bg-gray-200" />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src="/avatars/01.png" alt="Profile" />
                      <AvatarFallback>{getUserInitials(user?.name)}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user?.name || 'User'}</p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user?.email || 'user@landscapeflow.com'}
                      </p>
                      <p className="text-xs leading-none text-brand-600 font-medium capitalize">
                        {user?.role || 'user'}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings">Settings</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem>Support</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout}>Log out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-4 sm:py-6">
          <div className="mx-auto p-4 sm:p-6 lg:px-8">{children}</div>
        </main>
      </div>
    </div>
  );
}